/**
 * Serviço de Resolução de Diretório de Exportação
 * 
 * Implementa a cadeia de fallbacks para determinar o diretório base de exportação:
 * 1. regras.diretorio_base_export_prod (fonte primária)
 * 2. regras.diretorio_base_exportpng
 * 3. regras.diretorios.producao
 * 4. regras.diretorios.layout_png
 * 5. regrasSalvamento[0].diretorio_base_export_prod
 * 6. profile.output_dir (SOMENTE em desenvolvimento, com confirmação)
 * 
 * Registra telemetria para todos os fallbacks.
 */

import { sendToTerminal } from '../../../lib/terminalLogger';
import { requestConfirmation } from '../../../lib/globalConfirm';

export interface DirectoryResolutionResult {
  /** Diretório base resolvido */
  directory: string;
  
  /** Fonte de dados usada */
  source: string;
  
  /** Se true, usou uma fonte alternativa (fallback) */
  usedFallback: boolean;
}

export interface DirectoryResolverDependencies {
  /** Dados salvos da sessão */
  saved: any;
  
  /** Regras de salvamento do produto */
  regrasSalvamento?: any[];
  
  /** Perfil ativo (para fallback em dev) */
  activeProfile?: any;
  
  /** Formato de exportação (para buscar no perfil) */
  format?: string;
  
  /** Se true, permite fallback do perfil em dev */
  useFormatOverrides?: boolean;
  
  /** Formato manual (usado com useFormatOverrides) */
  manualFormat?: string;
}

/**
 * Resolve o diretório base de exportação aplicando a cadeia de fallbacks.
 * 
 * @throws {Error} Se nenhum diretório for encontrado
 */
export async function resolveExportDirectory(
  deps: DirectoryResolverDependencies
): Promise<DirectoryResolutionResult> {
  const { saved, regrasSalvamento, activeProfile, format, useFormatOverrides, manualFormat } = deps;
  
  // Aplicar cadeia de fallbacks
  let baseFromRules = saved?.regras?.diretorio_base_export_prod
    || saved?.regras?.diretorio_base_exportpng
    || saved?.regras?.diretorios?.producao
    || saved?.regras?.diretorios?.layout_png
    || (regrasSalvamento && regrasSalvamento.length > 0 ? regrasSalvamento[0].diretorio_base_export_prod : undefined);

  // Identificar qual fonte foi usada (para telemetria)
  let usedFallbackSource = 'diretorio_base_export_prod';
  if (!saved?.regras?.diretorio_base_export_prod) {
    if (saved?.regras?.diretorio_base_exportpng) usedFallbackSource = 'diretorio_base_exportpng';
    else if (saved?.regras?.diretorios?.producao) usedFallbackSource = 'diretorios.producao';
    else if (saved?.regras?.diretorios?.layout_png) usedFallbackSource = 'diretorios.layout_png';
    else if (regrasSalvamento && regrasSalvamento.length > 0 && regrasSalvamento[0].diretorio_base_export_prod) {
      usedFallbackSource = 'regrasSalvamento[0]';
    }
  }

  // Fallback de perfil SOMENTE em dev e com confirmação
  if (!baseFromRules) {
    try {
      const isDev = import.meta?.env?.MODE !== 'production';
      
      if (isDev) {
        const fmt = String(((useFormatOverrides && manualFormat) ? manualFormat : format || 'tiff')).toLowerCase();
        const profileOptions = (activeProfile?.options as any)?.[fmt] || null;
        const profileBase = profileOptions?.output_dir || profileOptions?.base_dir;
        
        if (profileBase && typeof profileBase === 'string') {
          // Importar telemetria
          const { logFallbackUsage, FallbackCategory, FallbackRisk } = await import('../../../lib/fallbackMonitor');
          
          logFallbackUsage({
            category: FallbackCategory.CONFIGURATION,
            risk: FallbackRisk.HIGH,
            location: 'ExportModal:baseDir',
            primarySource: 'regras.diretorio_base_export_prod',
            fallbackSource: 'profile.output_dir (DEV)',
            reason: 'Regras do produto não retornaram diretório base',
            timestamp: Date.now()
          });

          const confirmar = await requestConfirmation({
            title: 'Modo Desenvolvimento',
            message: 
              '⚠️ MODO DESENVOLVIMENTO\n\n' +
              'Diretório base não encontrado nas regras do produto.\n\n' +
              `Usar diretório do perfil?\n${profileBase}\n\n` +
              '(Esta opção não estará disponível em produção)',
            confirmLabel: 'Usar Perfil',
            cancelLabel: 'Cancelar'
          });
          
          if (confirmar) {
            baseFromRules = profileBase;
            usedFallbackSource = 'profile (dev)';
            try { sendToTerminal('⚠️ [EXPORT] Usando diretório do perfil (dev)', profileBase); } catch {}
          }
        }
      }
    } catch (e) {
      try { sendToTerminal('❌ [EXPORT] Erro ao tentar fallback de perfil', { error: String(e) }); } catch {}
    }
  }

  // FAIL HARD se não encontrou diretório
  if (!baseFromRules) {
    alert(
      'Erro: Diretório base de exportação não configurado.\n\n' +
      'Possíveis causas:\n' +
      '- Regras do produto incompletas no backend\n' +
      '- Produto sem configuração de exportação\n' +
      '- Falha ao carregar dados do salvamento\n\n' +
      'Ação necessária:\n' +
      '1. Execute o salvamento primeiro\n' +
      '2. Verifique as configurações do produto\n' +
      '3. Entre em contato com o suporte se o problema persistir'
    );
    
    try {
      sendToTerminal('❌ [EXPORT] Diretório base AUSENTE', { 
        savedRegras: saved?.regras, 
        regrasSalvamento,
        produtoId: saved?.produto?.id
      });
    } catch {}
    
    throw new Error('Diretório base de exportação não encontrado');
  }

  // Log se usou fonte alternativa (fallback)
  const usedFallback = usedFallbackSource !== 'diretorio_base_export_prod';
  if (usedFallback) {
    try {
      const { logFallbackUsage, FallbackCategory, FallbackRisk } = await import('../../../lib/fallbackMonitor');
      
      logFallbackUsage({
        category: FallbackCategory.CONFIGURATION,
        risk: FallbackRisk.MEDIUM,
        location: 'ExportModal:baseDir',
        primarySource: 'regras.diretorio_base_export_prod',
        fallbackSource: usedFallbackSource,
        reason: 'Fonte primária não disponível',
        timestamp: Date.now()
      });
      
      sendToTerminal('⚠️ [EXPORT] Usando fonte alternativa para diretório', { 
        fonte: usedFallbackSource, 
        baseFromRules 
      });
    } catch {}
  }

  return {
    directory: baseFromRules,
    source: usedFallbackSource,
    usedFallback
  };
}

